import FeatureFlag from "./FeatureFlag";

/**
 * Flip to false to load real data from the Platform Core API
 * (via the Command Center backend at api/FeatureFlag/GetAll).
 */
const USE_DUMMY_DATA = false;

/** Wire shape of a flag returned by api/FeatureFlag/GetAll (camelCase JSON). */
interface ConditionalFeatureFlagDetail {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  startDateTime: string | null;
  endDateTime: string | null;
}

interface GetAllFeatureFlagsResponse {
  items: ConditionalFeatureFlagDetail[];
}

function toFeatureFlag(detail: ConditionalFeatureFlagDetail): FeatureFlag {
  return {
    id: detail.id,
    name: detail.name,
    description: detail.description ?? "",
    startDate: detail.startDateTime ? new Date(detail.startDateTime) : null,
    endDate: detail.endDateTime ? new Date(detail.endDateTime) : null,
    enabled: detail.enabled,
  };
}

export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  if (USE_DUMMY_DATA) {
    return GetDummyData(5, 15);
  }

  const response = await fetch("api/FeatureFlag/GetAll");
  if (!response.ok) {
    throw new Error(`Failed to load feature flags (${response.status} ${response.statusText})`);
  }

  const body: GetAllFeatureFlagsResponse = await response.json();
  return (body.items ?? []).map(toFeatureFlag);
}

export async function saveFeatureFlags(featureFlags: FeatureFlag[]): Promise<Response> {
  // Saving is not hooked up to the Platform Core API yet (separate card),
  // so both modes use the dummy implementation for now.
  return DummySave(featureFlags);
}

async function DummySave(featureFlags: FeatureFlag[]): Promise<Response> {
  const seed = Math.floor(Math.random() * (2 - 1 + 1)) + 1;

  switch (seed) {
    case 1:
      return new Response(JSON.stringify({
        successful: true,
        ok: true,
        message: "Feature flags saved successfully",
        flags: featureFlags
      }), { status: 200, statusText: "OK" });
    case 2:
      return new Response(JSON.stringify({
        successful: false,
        ok: false,
        message: "Error saving feature flags",
        flags: featureFlags
      }), { status: 400, statusText: "Bad Request" });
    default:
      return new Response(JSON.stringify({ status: 500, successful: false, ok: false, message: "Unknown error", flags: [] }), { status: 500, statusText: "Internal Server Error" });
  }
}

async function GetDummyData(id: number, count: number = 1): Promise<FeatureFlag[]> {
  function createDummyFlags(countStart: number): FeatureFlag[] {
    return [
      {
        id: id + countStart,
        name: "test1",
        description: "Short description",
        startDate: new Date("2026-07-14T00:00:00.000-04:00"),
        endDate: null,
        enabled: true
      },
      {
        id: id + countStart + 1,
        name: "test2",
        description: "This is a very long description. It goes on for a while. There are details, instructions, and other information. It needs to be truncated or wrapped or both else the table will break",
        startDate: new Date("2026-07-14T00:00:00.000-04:00"),
        endDate: new Date("2026-07-18T20:00:00.000-04:00"),
        enabled: false
      },
      {
        id: id + countStart + 2,
        name: "test3",
        description: "Medium description, clarifying",
        startDate: new Date(),
        endDate: new Date(),
        enabled: true
      }
    ];
  }

  let dummyFlags: FeatureFlag[] = [];
  for (let i = 1; i < count; i++) {
    dummyFlags = [...dummyFlags, ...createDummyFlags((i - 1) * 3)];
  }

  return dummyFlags;
}
